import { UtmSources } from '~/constants/utm-sources';
import { TPartnerData, TPartner } from '~/apps/main/Partners/partners.types';
import { SignupPageLayout } from '~/components/Signup/types/signup.types';
import {
    SignupPagePrismmContent,
    PrismmCustomMobileLogo,
    PrismmSimplifiedIntakeFormCustomMobileLogo
} from '~/components/Signup/SignupPagePrismmContent';
import colors from '@honeybook/hbui/dist/styles/colors';
import AdobeSignupLogo from '~/components/Signup/components/SignupPage/Adobe/AdobeSignupLogo';
import AdobeSimplifiedMobileLogo from '~/components/Signup/components/SignupPage/Adobe/AdobeSimplifiedMobileLogo';
import { ZoomMobileLogo } from '~/components/Signup/components/SignupPage/Zoom/ZoomSignupLogo';
import ZoomSimplifiedIntakeFormCustomMobileLogo from '~/components/Signup/components/SignupPage/Zoom/ZoomSimplifiedIntakeFormCustomMobileLogo';
import { ShowitSignupLogo } from '~/components/Signup/components/SignupPage/Showit/ShowitSignupLogo';
import ShowitSimplifiedIntakeFormCustomMobileLogo from '~/components/Signup/components/SignupPage/Showit/ShowitSimplifiedIntakeFormCustomMobileLogo';
import { Coupons } from '~/constants/coupons';
import { GlobalBanner } from '~/components/GlobalBanner/types/global-banner.types';
import {
    SignupPageTimelineGeniusContent,
    TimelineGeniusCustomMobileLogo,
    TimelineGeniussimplifiedIntakeFormCustomMobileLogo
} from '~/components/Signup/Integrations/TimelineGenius/SignupPageTimelineGeniusContent';
import { SignupPageWevenContent } from '~/components/Signup/SignupPageWevenContent';

export const PARTNERS_CONFIG: Record<TPartner, TPartnerData> = {
    adobe: {
        name: 'Adobe',
        utmSource: UtmSources.Adobe,
        couponCode: Coupons.adobe50,
        welcomeModal: {
            file: 'AdobeWelcomeModalWrapper',
            triggerPage: 'home'
        },
        signup: {
            pageData: {
                illustrationPath:
                    'illustrations/intake-form-adobe-illustration.png',
                hasSubtitleTransKey: true,
                footerComponent: true,
                customDesktopLogo: AdobeSignupLogo,
                customMobileLogo: AdobeSignupLogo,
                hasTitleTransKey: true
            }
        }
    },
    freelancers_union: {
        name: 'Freelancers Union',
        utmSource: UtmSources.FreelancersUnion,
        couponCode: Coupons.freelancersunion,
        welcomeModal: {
            file: 'FreelancersUnionWelcomeModalWrapper',
            triggerPage: 'home'
        },
        signup: {
            layout: SignupPageLayout.FreelancersUnion,
            transKey: 'freelancers_union'
        }
    },
    prismm: {
        name: 'Prismm',
        utmSource: UtmSources.Prismm,
        couponCode: Coupons.prismm,
        welcomeModal: {
            file: 'PrismmWelcomeModalWrapper',
            triggerPage: 'home'
        },
        signup: {
            layout: SignupPageLayout.Original,
            pageData: {
                customContent: SignupPagePrismmContent,
                customMobileLogo: PrismmCustomMobileLogo,
                backgroundColor: colors.geyserLight
            },
            transKey: 'prismm',
            transKeyMobile: 'prismm_mobile',
            termsLink: 'https://www.honeybook.com/lp/terms-of-service-prismm'
        },
        integrationBannerKey: GlobalBanner.PrismmIntegration
    },
    pictime: {
        name: 'Pic-Time',
        utmSource: UtmSources.PicTime,
        couponCode: Coupons.pictime,
        welcomeModal: {
            file: 'PicTimeWelcomeModalWrapper',
            triggerPage: 'home'
        },
        offerLink:
            'https://help.honeybook.com/en/articles/8338497-pic-time-offer-faq',
        integrationBannerKey: GlobalBanner.PicTimeIntegration
    },
    john_branch: {
        name: 'John Branch',
        utmSource: UtmSources.JohnBranch,
        welcomeModal: {
            file: 'JohnBranchWelcomeModalWrapper',
            triggerPage: 'templates'
        },
        signup: {
            layout: SignupPageLayout.JohnBranch,
            transKey: 'john_branch'
        }
    },
    the_knot: {
        name: 'The Knot',
        utmSource: UtmSources.TheKnot,
        welcomeModal: {
            file: 'TheKnotWelcomeModalWrapper',
            triggerPage: 'home'
        },
        signup: {
            layout: SignupPageLayout.Original,
            pageData: {
                hasSubtitleTransKey: true,
                hasTitleTransKey: true
            }
        }
    },
    breakthrough_invite: {
        name: 'Breakthrough',
        utmSource: UtmSources.BreakthroughInvite,
        couponCode: Coupons.grow30
    },
    zoom: {
        name: 'Zoom',
        utmSource: UtmSources.Zoom,
        couponCode: Coupons.zoom60,
        welcomeModal: {
            file: 'ZoomWelcomeModalWrapper',
            triggerPage: 'home'
        },
        signup: {
            layout: SignupPageLayout.Original,
            pageData: {
                illustrationPath: '/illustrations/hb-zoom-integration-xl.png',
                customMobileLogo: ZoomMobileLogo,
                hasSubtitleTransKey: true,
                hasTitleTransKey: true,
                footerComponent: true
            }
        }
    },
    showit: {
        name: 'Showit',
        utmSource: UtmSources.Showit,
        couponCode: Coupons.showit2025,
        signup: {
            layout: SignupPageLayout.Original,
            pageData: {
                illustrationPath: '/illustrations/showit-hb-logo.png',
                customMobileLogo: ShowitSignupLogo,
                hasSubtitleTransKey: true,
                hasTitleTransKey: true,
                footerComponent: true
            }
        }
    },
    weven: {
        name: 'Weven',
        utmSource: UtmSources.Weven,
        couponCode: Coupons.weven,
        signup: {
            layout: SignupPageLayout.Original,
            pageData: {
                customContent: SignupPageWevenContent,
                backgroundColor: colors.geyserLight
            },
            transKey: 'weven',
            termsLink: 'https://www.honeybook.com/lp/policy/weven'
        }
    },
    timeline_genius: {
        name: 'Timeline Genius',
        utmSource: UtmSources.TimelineGenius,
        couponCode: Coupons.timelinegenius1yf,
        welcomeModal: {
            file: 'TimelineGeniusExistingMemberWelcomeModalWrapper',
            triggerPage: 'home'
        },
        signup: {
            layout: SignupPageLayout.Original,
            pageData: {
                customContent: SignupPageTimelineGeniusContent,
                customMobileLogo: TimelineGeniusCustomMobileLogo,
                backgroundColor: colors.geyserLight
            },
            transKey: 'timeline_genius',
            transKeyMobile: 'timeline_genius_mobile',
            termsLink: 'https://www.honeybook.com/lp/policy/timeline-genius'
        },
        offerLink:
            'https://help.honeybook.com/en/articles/10808672-timeline-genius-partnership',
        integrationBannerKey: GlobalBanner.TimelineGeniusIntegration
    },
    timeline_genius_f1: {
        name: 'Timeline Genius',
        utmSource: UtmSources.TimelineGeniusFlow1,
        welcomeModal: {
            file: 'TimelineGeniusNewMemberWelcomeModalWrapper',
            triggerPage: 'home'
        },
        offerLink:
            'https://help.honeybook.com/en/articles/10808672-timeline-genius-partnership',
        integrationBannerKey: GlobalBanner.TimelineGeniusIntegration
    },
    blaze_ai: {
        name: 'Blaze.ai',
        utmSource: UtmSources.Blazeai,
        couponCode: Coupons.blazeai30
    },
    rella: {
        name: 'Rella',
        utmSource: UtmSources.Rella,
        couponCode: Coupons.rella30
    }
} as const;

export const PARTNERS_CONFIG_QUIZ: Record<TPartner, TPartnerData> = {
    adobe: {
        name: 'Adobe',
        utmSource: UtmSources.Adobe,
        couponCode: Coupons.adobe50,
        welcomeModal: {
            file: 'AdobeWelcomeModalWrapper',
            triggerPage: 'home'
        },
        signup: {
            pageData: {
                illustrationPath:
                    'illustrations/intake-form-adobe-illustration.png',
                hasSubtitleTransKey: true,
                footerComponent: true,
                customDesktopLogo: AdobeSignupLogo,
                customMobileLogo: AdobeSimplifiedMobileLogo,
                hasTitleTransKey: true
            }
        }
    },
    freelancers_union: {
        name: 'Freelancers Union',
        utmSource: UtmSources.FreelancersUnion,
        couponCode: Coupons.freelancersunion,
        welcomeModal: {
            file: 'FreelancersUnionWelcomeModalWrapper',
            triggerPage: 'home'
        },
        signup: {
            layout: SignupPageLayout.FreelancersUnion,
            transKey: 'freelancers_union'
        }
    },
    prismm: {
        name: 'Prismm',
        utmSource: UtmSources.Prismm,
        couponCode: Coupons.prismm,
        welcomeModal: {
            file: 'PrismmWelcomeModalWrapper',
            triggerPage: 'home'
        },
        signup: {
            layout: SignupPageLayout.Original,
            pageData: {
                customContent: SignupPagePrismmContent,
                customMobileLogo: PrismmSimplifiedIntakeFormCustomMobileLogo,
                backgroundColor: colors.geyserLight
            },
            transKey: 'prismm',
            transKeyMobile: 'prismm_mobile',
            termsLink: 'https://www.honeybook.com/lp/terms-of-service-prismm'
        },
        integrationBannerKey: GlobalBanner.PrismmIntegration
    },
    pictime: {
        name: 'Pic-Time',
        utmSource: UtmSources.PicTime,
        couponCode: Coupons.pictime,
        welcomeModal: {
            file: 'PicTimeWelcomeModalWrapper',
            triggerPage: 'home'
        },
        offerLink:
            'https://help.honeybook.com/en/articles/8338497-pic-time-offer-faq',
        integrationBannerKey: GlobalBanner.PicTimeIntegration
    },
    john_branch: {
        name: 'John Branch',
        utmSource: UtmSources.JohnBranch,
        welcomeModal: {
            file: 'JohnBranchWelcomeModalWrapper',
            triggerPage: 'templates'
        },
        signup: {
            layout: SignupPageLayout.JohnBranch,
            transKey: 'john_branch'
        }
    },
    the_knot: {
        name: 'The Knot',
        utmSource: UtmSources.TheKnot,
        welcomeModal: {
            file: 'TheKnotWelcomeModalWrapper',
            triggerPage: 'home'
        },
        signup: {
            layout: SignupPageLayout.Original,
            pageData: {
                hasSubtitleTransKey: true,
                hasTitleTransKey: true
            }
        }
    },
    breakthrough_invite: {
        name: 'Breakthrough',
        utmSource: UtmSources.BreakthroughInvite,
        couponCode: Coupons.grow30
    },
    zoom: {
        name: 'Zoom',
        utmSource: UtmSources.Zoom,
        couponCode: Coupons.zoom60,
        welcomeModal: {
            file: 'ZoomWelcomeModalWrapper',
            triggerPage: 'home'
        },
        signup: {
            layout: SignupPageLayout.Original,
            pageData: {
                illustrationPath: '/illustrations/hb-zoom-integration-xl.png',
                customMobileLogo: ZoomSimplifiedIntakeFormCustomMobileLogo,
                hasSubtitleTransKey: true,
                hasTitleTransKey: true,
                footerComponent: true
            }
        }
    },
    showit: {
        name: 'Showit',
        utmSource: UtmSources.Showit,
        couponCode: Coupons.showit2025,
        signup: {
            layout: SignupPageLayout.Original,
            pageData: {
                illustrationPath: '/illustrations/showit-hb-logo.png',
                customMobileLogo: ShowitSimplifiedIntakeFormCustomMobileLogo,
                hasSubtitleTransKey: true,
                hasTitleTransKey: true,
                footerComponent: true
            }
        }
    },
    weven: {
        name: 'Weven',
        utmSource: UtmSources.Weven,
        couponCode: Coupons.weven,
        signup: {
            layout: SignupPageLayout.Original,
            pageData: {
                customContent: SignupPageWevenContent,
                backgroundColor: colors.geyserLight
            },
            transKey: 'weven',
            termsLink: 'https://www.honeybook.com/lp/policy/weven'
        }
    },
    timeline_genius: {
        name: 'Timeline Genius',
        utmSource: UtmSources.TimelineGenius,
        couponCode: Coupons.timelinegenius1yf,
        welcomeModal: {
            file: 'TimelineGeniusExistingMemberWelcomeModalWrapper',
            triggerPage: 'home'
        },
        signup: {
            layout: SignupPageLayout.Original,
            pageData: {
                customContent: SignupPageTimelineGeniusContent,
                customMobileLogo:
                    TimelineGeniussimplifiedIntakeFormCustomMobileLogo,
                backgroundColor: colors.geyserLight
            },
            transKey: 'timeline_genius',
            transKeyMobile: 'timeline_genius_mobile',
            termsLink: 'https://www.honeybook.com/lp/policy/timeline-genius'
        },
        offerLink:
            'https://help.honeybook.com/en/articles/10808672-timeline-genius-partnership',
        integrationBannerKey: GlobalBanner.TimelineGeniusIntegration
    },
    timeline_genius_f1: {
        name: 'Timeline Genius',
        utmSource: UtmSources.TimelineGeniusFlow1,
        welcomeModal: {
            file: 'TimelineGeniusNewMemberWelcomeModalWrapper',
            triggerPage: 'home'
        },
        offerLink:
            'https://help.honeybook.com/en/articles/10808672-timeline-genius-partnership',
        integrationBannerKey: GlobalBanner.TimelineGeniusIntegration
    },
    blaze_ai: {
        name: 'Blaze.ai',
        utmSource: UtmSources.Blazeai,
        couponCode: Coupons.blazeai30
    },
    rella: {
        name: 'Rella',
        utmSource: UtmSources.Rella,
        couponCode: Coupons.rella30
    }
} as const;
